import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Grid,
  Typography,
  Snackbar,
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import {
  useMutation,
  useManualQuery,
} from 'graphql-hooks';
import { useForm } from 'react-hook-form';
import { useAtomValue } from 'jotai';

import { UPDATE_PASSWORD } from '../../graphql/user/mutation';
import { USER_EMAIL_EXIST } from '../../graphql/user/query';
import { CREATE_CODE, VALIDATE_CODE } from '../../graphql/code/mutation';
import { CustomInput } from '../CustomInput';
import { CodeType } from '../../types';
import { isMobileAtom } from '../../atoms';
import { authTitle } from '../../theme';
import styles from './style';

interface ForgotPasswordEmail {
  email: string;
}

interface ForgotPasswordCode {
  code: string;
}

interface ForgotPasswordData {
  password: string;
}

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface ForgotPasswordFormProps {
  // In the login popup: the popup shows its own title.
  embedded?: boolean;
  // Replaces the link to the sign up with a link back to the login (ex: the popup's login view).
  onBackToLogin?: () => void;
  // Replaces the navigation to /login once the new password is saved (ex: the popup's login view).
  onDone?: () => void;
}

// Forgot password form: email, then the code received, then the new password. Fills its container.
const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = React.memo((props) => {
  const {
    embedded = false,
    onBackToLogin,
    onDone,
  } = props;
  const { t } = useTranslation();
  const isMobile = useAtomValue(isMobileAtom);
  // On a phone there is no sign up (it happens in the app): back to the login instead.
  const showLoginLink = !!onBackToLogin || isMobile;
  const [errorMessage, setErrorMessage] = useState({ active: false, msg: '' });
  const [successMessage, setSuccessMessage] = useState({ active: false, msg: '' });
  const [isEmailFill, setIsEmailFill] = useState(false);
  const [isCodeFill, setIsCodeFill] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [userEmailExist] = useManualQuery(USER_EMAIL_EXIST);
  const [userUpdatePassword] = useMutation(UPDATE_PASSWORD);
  const [createCode] = useMutation(CREATE_CODE);
  const [validateCode] = useMutation(VALIDATE_CODE);
  const navigate = useNavigate();

  const titleTrad = t('forgotPassword.title');
  const subtitleTrad = t('forgotPassword.subtitle');
  const getCodeTrad = t('forgotPassword.getCode');
  const changePasswordTrad = t('forgotPassword.changePassword');
  const incorrectCodeTitle = t('form.error.incorrect.code');
  const notExistEmailTitle = t('form.error.notExist.email');
  const codeSentTitle = t('form.codeSent');
  const passwordSentTitle = t('form.passwordSent');
  const formValidateCodeTrad = t('form.validateCode');
  const formErrorRequiredEmailTrad = t('form.error.required.email');
  const formErrorPatternEmailTrad = t('form.error.pattern.email');
  const emailTrad = t('email');
  const codeTrad = t('code');
  const formErrorRequiredCodeTrad = t('form.error.required.code');
  const formErrorMinLengthCodeTrad = t('form.error.minLength.code');
  const formPasswordTrad = t('form.password');
  const formErrorRequiredPasswordTrad = t('form.error.required.password');
  const formErrorMinLengthPasswordTrad = t('form.error.minLength.password');
  const formErrorPatternPasswordTrad = t('form.error.pattern.password');
  const formResumeTitleTrad = t('form.resumeTitle');
  const formSignUpTrad = t('form.signUp');
  const formSignUpQuestionTrad = t('form.signUpQuestion');
  const formSignInQuestionTrad = t('form.signInQuestion');
  const formSignInTrad = t('form.signIn');

  const {
    control,
    handleSubmit,
  } = useForm<ForgotPasswordEmail>();

  const {
    control: controlCode,
    handleSubmit: handleCode,
  } = useForm<ForgotPasswordCode>();

  const {
    control: controlPassword,
    handleSubmit: handlePassword,
  } = useForm<ForgotPasswordData>();

  const onSubmitEmail = useCallback(async (obj: ForgotPasswordEmail) => {
    try {
      const { data } = await userEmailExist({
        variables: {
          email: obj.email,
        },
      });
      if (data.userEmailExist) {
        const { data: code } = await createCode({
          variables: {
            input: {
              email: obj.email,
              type: CodeType.EMAIL,
            },
          },
        });
        if (code.createCode && code.createCode.id) {
          setSuccessMessage({ active: true, msg: codeSentTitle });
          setEmail(obj.email);
          setIsEmailFill(true);
        }
      } else {
        setErrorMessage({ active: true, msg: notExistEmailTitle });
        setIsEmailFill(false);
      }
    } catch (err: any) {
      if (err && err.message === 'UPG') {
        setErrorMessage({ active: true, msg: t('upgError') });
      } else if (err && err.message === 'UPA') {
        setErrorMessage({ active: true, msg: t('upaError') });
      } else if (err && err.message === 'UPB') {
        setErrorMessage({ active: true, msg: t('upbError') });
      } else if (err && err.message) {
        setErrorMessage({ active: true, msg: err.message });
      }
    }
  }, [
    t,
    codeSentTitle,
    createCode,
    notExistEmailTitle,
    userEmailExist
  ]);

  const onSubmitCode = useCallback(async (obj: ForgotPasswordCode) => {
    try {
      const { data } = await validateCode({
        variables: {
          input: {
            email,
            value: parseInt(obj.code, 10),
            type: CodeType.EMAIL,
          },
        },
      });
      if (data.validateCode) {
        setIsCodeFill(true);
        setCode(obj.code);
      } else {
        setErrorMessage({ active: true, msg: incorrectCodeTitle });
      }
    } catch (e: any) {
      setErrorMessage({ active: true, msg: e.message });
    }
  }, [email, validateCode, incorrectCodeTitle])

  const onSubmitPassword = useCallback(async (obj: ForgotPasswordData) => {
    try {
      const { data } = await userUpdatePassword({
        variables: {
          input: {
            email,
            code: parseInt(code, 10),
            password: obj.password,
          },
        },
      });
      if (data.userUpdatePassword) {
        if (onDone) {
          onDone();
          return;
        }
        navigate('/login', { replace: true });
        setSuccessMessage({ active: true, msg: passwordSentTitle });
      }
    } catch (e: any) {
      setErrorMessage({ active: true, msg: e.message });
    }
  }, [
    navigate,
    email,
    onDone,
    userUpdatePassword,
    code,
    passwordSentTitle
  ]);

  // Handler of the current step: email, then code, then new password.
  const onClickSubmit = useMemo(() => {
    if (isEmailFill && isCodeFill) {
      return handlePassword(onSubmitPassword);
    }
    if (isEmailFill) {
      return handleCode(onSubmitCode);
    }
    return handleSubmit(onSubmitEmail);
  }, [
    isEmailFill,
    isCodeFill,
    handleCode,
    handlePassword,
    handleSubmit,
    onSubmitCode,
    onSubmitEmail,
    onSubmitPassword
  ]);

  // Email: the code is sent; code: it is checked; then the new password is saved.
  const titleButton = useMemo(() => {
    if (isEmailFill && isCodeFill) {
      return changePasswordTrad;
    }
    if (isEmailFill) {
      return formValidateCodeTrad;
    }
    return getCodeTrad;
  }, [
    changePasswordTrad,
    formValidateCodeTrad,
    getCodeTrad,
    isEmailFill,
    isCodeFill,
  ]);

  const onCloseErrorSnack = useCallback(() => {
    setErrorMessage({ active: false, msg: '' });
  }, []);

  const onCloseSuccessSnack = useCallback(() => {
    setSuccessMessage({ active: false, msg: '' });
  }, []);

  const emailRules = useMemo(() => ({
    required: formErrorRequiredEmailTrad,
    pattern: {
      value: EMAIL_REGEX,
      message: formErrorPatternEmailTrad,
    },
  }), [
    formErrorRequiredEmailTrad,
    formErrorPatternEmailTrad,
  ]);

  const codeRules = useMemo(() => ({
    required: formErrorRequiredCodeTrad,
    minLength: {
      value: 4,
      message: formErrorMinLengthCodeTrad,
    },
  }), [
    formErrorRequiredCodeTrad,
    formErrorMinLengthCodeTrad,
  ]);

  const passwordRules = useMemo(() => ({
    required: formErrorRequiredPasswordTrad,
    minLength: {
      value: 8,
      message: formErrorMinLengthPasswordTrad,
    },
    pattern: {
      value: PASSWORD_REGEX,
      message: formErrorPatternPasswordTrad,
    },
  }), [
    formErrorRequiredPasswordTrad,
    formErrorMinLengthPasswordTrad,
    formErrorPatternPasswordTrad,
  ]);

  const formSignUpSx = useMemo(() => ({
    ...styles.smallLink,
    marginLeft: isMobile ? '1vw' : '0.5vw',
  }), [isMobile]);

  const onClickBackToLogin = useCallback((e: React.MouseEvent) => {
    if (onBackToLogin) {
      e.preventDefault();
      onBackToLogin();
    }
  }, [onBackToLogin]);

  return (
    <>
      <Grid container>
        <Grid item xs={12} mt={0}>
          {!embedded && (
            <Typography variant="h4" align="center" sx={authTitle}>
              {titleTrad}
            </Typography>
          )}
          {/* Same words as the link clicked on the login page, then what happens. */}
          {!isEmailFill && <Typography align="center" sx={styles.subtitle}>{subtitleTrad}</Typography>}
        </Grid>
        <Grid item mt={3} xs={12}>
          <CustomInput
            dark
            name="email"
            placeholder={emailTrad}
            control={control}
            label={'Email'}
            rules={emailRules}
            fullWidth
            variant="outlined"
          />
        </Grid>
        {isEmailFill && (
          <Grid item xs={12} mt={2}>
            <CustomInput
              dark
              name="code"
              label={codeTrad}
              placeholder={codeTrad}
              disabled={!isEmailFill}
              control={controlCode}
              rules={codeRules}
              fullWidth
              variant="outlined"
            />
          </Grid>
        )}
        {isEmailFill && isCodeFill && (
          <Grid item mt={2} xs={12}>
            <CustomInput
              dark
              name="password"
              control={controlPassword}
              disabled={!isCodeFill}
              label={formPasswordTrad}
              placeholder={formPasswordTrad}
              rules={passwordRules}
              fullWidth
              variant="outlined"
              type="password"
              required
            />
            <Typography sx={styles.hint}>
              {formResumeTitleTrad}
            </Typography>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          mt={2}
        >
          <Button
            onClick={onClickSubmit}
            fullWidth
            sx={styles.validateButton}
          >
            <Typography fontWeight="medium" fontFamily="Poppins">
              {titleButton}
            </Typography>
          </Button>
        </Grid>
        <Grid
          item
          mt={1.5}
          mb={0}
          xs={12}
          flexDirection="row"
          display="flex"
          justifyContent="flex-end"
        >
          <Typography display="inline" sx={styles.smallText}>
            {showLoginLink ? formSignInQuestionTrad : formSignUpQuestionTrad}
          </Typography>
          <Typography
            display="inline"
            sx={formSignUpSx}
          >
            {showLoginLink ? (
              <Link to="/login" style={styles.linkTypo} onClick={onClickBackToLogin}>
                {formSignInTrad}
              </Link>
            ) : (
              <Link to="/signup" style={styles.linkTypo}>
                {formSignUpTrad}
              </Link>
            )}
          </Typography>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={styles.anchorOrigin}
        sx={styles.toast}
        open={errorMessage.active}
        autoHideDuration={3000}
        message={errorMessage.msg}
        onClose={onCloseErrorSnack}
      >
        <Alert severity="error" sx={styles.alert}>
          {errorMessage.msg}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={styles.anchorOrigin}
        sx={styles.toast}
        open={successMessage.active}
        autoHideDuration={3000}
        message={successMessage.msg}
        onClose={onCloseSuccessSnack}
      >
        <Alert severity="success" sx={styles.alert}>
          {successMessage.msg}
        </Alert>
      </Snackbar>
    </>
  );
});

export default ForgotPasswordForm;
